import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import { DrawerContainer } from '../../../components/Drawer/DrawerContainer';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import Loading from '../../../components/Loading';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import useTranslate from '../../../hooks/useTranslate';
import { useRoles } from '../../../hooks/useRoles';
import useRoutePrefix from '../../../hooks/useRoutePrefix';
import { useGetSiniestroVisitasQuery } from '../../../services/roamApi';
import {
    useAssignPreventorMutation,
    useCancelVisitMutation,
    useDownloadVisitaActaMutation,
    useGetPreventorsQuery,
    usePresentVisitMutation,
    useReassignPreventorMutation,
    useSendVisitToRevisionMutation
} from '../../../services/visitasApi';
import { formatFullName, getDate } from '../../../utils/common';
import {
    getVisitaEstadoChipSx,
    VisitaEstado,
    visitaEstadoChipMap
} from '../../Visitas/consts/visitasStatus';
import useAlerts from '../../Visitas/hooks/useAlerts';
import { soloPreventoresActivos } from '../../Visitas/utils/preventoresActivos';
import { SiniestroItem, SiniestroStatus } from '../types/siniestros.types';

interface ExpressVisitManagementDrawerProps {
    open: boolean;
    onClose: () => void;
    siniestro: SiniestroItem | null;
    activeTab: SiniestroStatus;
    onSuccess: () => void;
}

const VISIT_DETAIL_VISIBLE_STATES = [1, 2, 3, 4, 5, 7, 9, 10, 11];

const getVisitDate = (visita: any) =>
    visita?.fechaVisita || visita?.fechaProgramada || visita?.fecha || null;

const getEstablishmentName = (visita: any) =>
    visita?.establecimiento?.nombre || visita?.nombreEstablecimiento || '-';

const getVisitTypeDescription = (visita: any) => {
    if (Array.isArray(visita?.actividades) && visita.actividades.length > 0) {
        if (visita.actividades.length === 1) {
            return visita.actividades[0]?.tipoVisita?.descripcion || '-';
        }

        const descriptions = visita.actividades
            .map((actividad: any) => actividad?.tipoVisita?.descripcion)
            .filter(Boolean);

        return descriptions.length ? descriptions.join(', ') : '-';
    }

    return visita?.tipoVisita?.descripcion || visita?.nombreTipoVisita || visita?.tipoVisita || '-';
};

const getVisitTypeId = (visita: any) => {
    if (visita?.idTipoVisita) return Number(visita.idTipoVisita);
    if (visita?.tipoVisita?.id) return Number(visita.tipoVisita.id);
    if (Array.isArray(visita?.actividades) && visita.actividades.length > 0) {
        return Number(visita.actividades[0]?.tipoVisita?.id) || undefined;
    }
    return undefined;
};

const getPreventorName = (visita: any) => {
    const nombre = visita?.preventor?.nombre || '';
    const apellido = visita?.preventor?.apellido || '';
    return `${nombre} ${apellido}`.trim();
};

function ExpressVisitManagementDrawer({
    open,
    onClose,
    siniestro,
    activeTab,
    onSuccess
}: ExpressVisitManagementDrawerProps) {
    const { t } = useTranslation('siniestrosRoam');
    const { t: tVisitas } = useTranslation('visitas');
    const { translate } = useTranslate(tVisitas);
    const { showCancelVisitConfirmationAlert } = useAlerts({ translate });
    const navigate = useNavigate();
    const { isSupervisorSIMP } = useRoles();
    const routePrefix = useRoutePrefix();
    const [editingVisitId, setEditingVisitId] = useState<number | null>(null);
    const [reassigningVisitId, setReassigningVisitId] = useState<number | null>(null);
    const [rejectingVisitId, setRejectingVisitId] = useState<number | null>(null);
    const [selectedPreventorByVisit, setSelectedPreventorByVisit] = useState<Record<number, any>>(
        {}
    );
    const [reassignMotiveByVisit, setReassignMotiveByVisit] = useState<Record<number, string>>({});
    const [rejectReasonByVisit, setRejectReasonByVisit] = useState<Record<number, string>>({});
    const [savingVisitId, setSavingVisitId] = useState<number | null>(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuVisit, setMenuVisit] = useState<any>(null);

    const siniestroId = siniestro?.id ? Number(siniestro.id) : null;
    const {
        data: visitas = [],
        isFetching,
        isError
    } = useGetSiniestroVisitasQuery(siniestroId as number, {
        skip: !open || !siniestroId,
        refetchOnMountOrArgChange: true
    });

    const { data: preventors, isFetching: isFetchingPreventors } = useGetPreventorsQuery(
        { take: 9999999, skip: 0 },
        { skip: isSupervisorSIMP }
    );
    const [assignPreventor] = useAssignPreventorMutation();
    const [reassignPreventor] = useReassignPreventorMutation();
    const [cancelVisit, { isLoading: isCancellingVisit }] = useCancelVisitMutation();
    const [downloadVisitaActa] = useDownloadVisitaActaMutation();
    const [presentVisit] = usePresentVisitMutation();
    const [sendVisitToRevision] = useSendVisitToRevisionMutation();

    // Para asignar o reasignar solo se ofrecen preventores activos (SMP-428).
    const preventorOptions =
        soloPreventoresActivos<any>(preventors?.data).map((preventor: any) => ({
            id: preventor.idUsuario,
            nombre: formatFullName(preventor.nombre, preventor.apellido),
            value: preventor.idUsuario
        }));

    // El supervisor solo consulta: ve las visitas sin acciones de gestión (SMP-460)
    const isReadOnlyTab =
        isSupervisorSIMP || activeTab === 'FINALIZED' || activeTab === 'CANCELLED';
    const pendingVisits = visitas.filter(
        (visita: any) => visita?.idEstado === VisitaEstado.SIN_ASIGNAR
    );
    const visibleVisits = activeTab === 'PENDING' ? pendingVisits : visitas;
    const firstEstablishmentName = visibleVisits.length
        ? getEstablishmentName(visibleVisits[0])
        : '-';

    const resetCardState = (visitId: number) => {
        setEditingVisitId((current) => (current === visitId ? null : current));
        setReassigningVisitId((current) => (current === visitId ? null : current));
        setRejectingVisitId((current) => (current === visitId ? null : current));
        setSelectedPreventorByVisit((current) => {
            const next = { ...current };
            delete next[visitId];
            return next;
        });
        setReassignMotiveByVisit((current) => {
            const next = { ...current };
            delete next[visitId];
            return next;
        });
        setRejectReasonByVisit((current) => {
            const next = { ...current };
            delete next[visitId];
            return next;
        });
    };

    const handleStartAssign = (visitId: number) => {
        setRejectingVisitId((current) => (current === visitId ? null : current));
        setReassigningVisitId((current) => (current === visitId ? null : current));
        setEditingVisitId(visitId);
    };

    const handleStartReassign = (visitId: number) => {
        setEditingVisitId((current) => (current === visitId ? null : current));
        setRejectingVisitId((current) => (current === visitId ? null : current));
        setReassigningVisitId(visitId);
    };

    const handleCancelAssign = (visitId: number) => {
        resetCardState(visitId);
    };

    const handleCancelVisit = (visitId: number) => {
        showCancelVisitConfirmationAlert({
            confirmAction: async (observacion: string) => {
                try {
                    setSavingVisitId(visitId);
                    await cancelVisit({ visitId, observacion }).unwrap();

                    enqueueSnackbar(tVisitas('drawer.successCancel'), {
                        variant: 'success'
                    });

                    resetCardState(visitId);
                    onClose();
                    onSuccess();
                } catch (error: any) {
                    enqueueSnackbar(error?.data?.message || tVisitas('drawer.errorUpdate'), {
                        variant: 'error'
                    });
                } finally {
                    setSavingVisitId(null);
                }
            }
        });
    };

    const handleSelectPreventor = (visitId: number, value: any) => {
        setSelectedPreventorByVisit((current) => ({
            ...current,
            [visitId]: value
        }));
    };

    const handleReassignMotiveChange = (visitId: number, value: string) => {
        setReassignMotiveByVisit((current) => ({
            ...current,
            [visitId]: value
        }));
    };

    const handleAssignPreventor = async (visit: any) => {
        const selectedPreventor = selectedPreventorByVisit[visit.id];

        if (!selectedPreventor?.id) {
            enqueueSnackbar(t('expressDrawer.alerts.selectPreventor'), { variant: 'warning' });
            return;
        }

        try {
            setSavingVisitId(visit.id);
            await assignPreventor({
                preventorID: selectedPreventor.id,
                body: {
                    idsVisita: [visit.id],
                    observacion: ''
                }
            }).unwrap();

            enqueueSnackbar(t('expressDrawer.alerts.assignSuccess'), {
                variant: 'success'
            });

            resetCardState(visit.id);
            onClose();
            onSuccess();
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('expressDrawer.alerts.assignError'), {
                variant: 'error'
            });
        } finally {
            setSavingVisitId(null);
        }
    };

    const handleReassignPreventor = async (visit: any) => {
        const selectedPreventor = selectedPreventorByVisit[visit.id];
        const motivo = reassignMotiveByVisit[visit.id]?.trim() || '';

        if (!selectedPreventor?.id) {
            enqueueSnackbar(t('expressDrawer.alerts.selectPreventor'), { variant: 'warning' });
            return;
        }

        if (motivo.length < 10 || motivo.length > 300) {
            enqueueSnackbar('El motivo de reasignación debe tener entre 10 y 300 caracteres', {
                variant: 'warning'
            });
            return;
        }

        try {
            setSavingVisitId(visit.id);
            await reassignPreventor({
                preventorID: selectedPreventor.id,
                body: {
                    idsVisita: [visit.id],
                    observacion: '',
                    motivo
                }
            }).unwrap();

            enqueueSnackbar(tVisitas('drawers.alerts.reassignPreventor.success'), {
                variant: 'success'
            });

            resetCardState(visit.id);
            onClose();
            onSuccess();
        } catch (error: any) {
            enqueueSnackbar(
                error?.data?.message || tVisitas('drawers.alerts.reassignPreventor.error'),
                {
                    variant: 'error'
                }
            );
        } finally {
            setSavingVisitId(null);
        }
    };

    const handleStartReject = (visitId: number) => {
        setEditingVisitId((current) => (current === visitId ? null : current));
        setRejectingVisitId(visitId);
    };

    const handleRejectReasonChange = (visitId: number, value: string) => {
        setRejectReasonByVisit((current) => ({
            ...current,
            [visitId]: value
        }));
    };

    const handleApproveVisit = async (visitId: number) => {
        try {
            setSavingVisitId(visitId);
            await presentVisit({ visitId }).unwrap();

            enqueueSnackbar(t('expressDrawer.alerts.approveSuccess'), {
                variant: 'success'
            });

            resetCardState(visitId);
            onClose();
            onSuccess();
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('expressDrawer.alerts.approveError'), {
                variant: 'error'
            });
        } finally {
            setSavingVisitId(null);
        }
    };

    const handleOpenVisitMenu = (event: React.MouseEvent<HTMLElement>, visita: any) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuVisit(visita);
    };

    const handleCloseVisitMenu = () => {
        setMenuAnchorEl(null);
        setMenuVisit(null);
    };

    const handleOpenVisitDetail = (visita: any) => {
        handleCloseVisitMenu();
        onClose();
        navigate(`/${routePrefix}/visitas/ejecucion`, {
            state: { selectedVisita: visita, siniestro: visita?.siniestro || { id: siniestro?.id } }
        });
    };

    const handleRejectVisit = async (visitId: number) => {
        const motivo = rejectReasonByVisit[visitId]?.trim() || '';

        if (!motivo) {
            enqueueSnackbar(t('expressDrawer.alerts.rejectReasonRequired'), { variant: 'warning' });
            return;
        }

        try {
            setSavingVisitId(visitId);
            await sendVisitToRevision({
                visitId,
                body: { observacion: motivo }
            }).unwrap();

            enqueueSnackbar(t('expressDrawer.alerts.requestChangesSuccess'), {
                variant: 'success'
            });

            resetCardState(visitId);
            onClose();
            onSuccess();
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('expressDrawer.alerts.requestChangesError'), {
                variant: 'error'
            });
        } finally {
            setSavingVisitId(null);
        }
    };

    const handleDownloadActa = async (visita: any) => {
        const idTipoVisita = getVisitTypeId(visita);

        if (!visita?.id || !idTipoVisita) {
            enqueueSnackbar(t('expressDrawer.alerts.downloadActaError'), { variant: 'error' });
            return;
        }

        try {
            await downloadVisitaActa({
                idVisita: Number(visita.id),
                idTipoVisita,
                tipoActa: 1
            })
                .unwrap()
                .then((response: any) => {
                    if (!response?.data) return;

                    const byteCharacters = atob(response.data);
                    const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
                    const blob = new Blob([new Uint8Array(byteNumbers)], {
                        type: 'application/pdf'
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'Acta_Visita.pdf');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                });

            handleCloseVisitMenu();
            enqueueSnackbar(t('expressDrawer.alerts.downloadActaSuccess'), { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('expressDrawer.alerts.downloadActaError'), {
                variant: 'error'
            });
        }
    };

    return (
        <DrawerContainer open={open} setOpen={onClose} onClose={onClose}>
            <Box display="flex" flexDirection="column" height="100%">
                <DrawerHeader onIconClick={onClose} text={t('expressDrawer.title')} />

                <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#101828', fontWeight: 700 }}>
                        {siniestro?.empleado?.nombre || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#667085', mt: 0.5 }}>
                        {firstEstablishmentName} - {siniestro?.nroSiniestro || '-'}
                    </Typography>
                </Box>

                <Divider />

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {isFetching && (
                        <Box sx={{ py: 4 }}>
                            <Loading size="small" />
                        </Box>
                    )}

                    {isError && <Alert severity="error">{t('expressDrawer.loadError')}</Alert>}

                    {!isFetching && !isError && visibleVisits.length === 0 && (
                        <Alert severity="info">{t('expressDrawer.empty')}</Alert>
                    )}

                    {!isFetching &&
                        visibleVisits.map((visita: any) => {
                            const statusConfig =
                                visitaEstadoChipMap[visita?.idEstado as VisitaEstado] ||
                                visitaEstadoChipMap[VisitaEstado.SIN_ASIGNAR];
                            const isEditing = editingVisitId === visita.id;
                            const isReassigning = reassigningVisitId === visita.id;
                            const isRejecting = rejectingVisitId === visita.id;
                            const isSaving = savingVisitId === visita.id;
                            const selectedPreventor = selectedPreventorByVisit[visita.id] || null;
                            const reassignMotive = reassignMotiveByVisit[visita.id] || '';
                            const rejectReason = rejectReasonByVisit[visita.id] || '';
                            const canAssignPreventor =
                                !isReadOnlyTab && visita?.idEstado === VisitaEstado.SIN_ASIGNAR;
                            const canReassignPreventor =
                                !isReadOnlyTab && visita?.idEstado === VisitaEstado.PENDIENTE;
                            const canReviewVisit =
                                !isReadOnlyTab && visita?.idEstado === VisitaEstado.REALIZADA;
                            const canSeeVisitDetail = VISIT_DETAIL_VISIBLE_STATES.includes(
                                Number(visita?.idEstado)
                            );
                            const canDownloadActa = Boolean(visita?.id && getVisitTypeId(visita));

                            return (
                                <Box
                                    key={visita?.id}
                                    sx={{
                                        border: '1px solid #E4E7EC',
                                        borderRadius: 2,
                                        p: 2.5,
                                        backgroundColor: '#FFFFFF',
                                        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)'
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={8}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#98A2B3',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '.04em'
                                                }}
                                            >
                                                {t('expressDrawer.fields.visitType')}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{ mt: 0.5, fontWeight: 600, color: '#101828' }}
                                            >
                                                {getVisitTypeDescription(visita)}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#98A2B3',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '.04em'
                                                }}
                                            >
                                                {t('expressDrawer.fields.status')}
                                            </Typography>
                                            <Box sx={{ mt: 0.75 }}>
                                                <Chip
                                                    label={statusConfig.text}
                                                    size="small"
                                                    sx={getVisitaEstadoChipSx(statusConfig)}
                                                />
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#98A2B3',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '.04em'
                                                }}
                                            >
                                                {t('expressDrawer.fields.preventor')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 0.5,
                                                    color:
                                                        visita?.idEstado ===
                                                        VisitaEstado.SIN_ASIGNAR
                                                            ? statusConfig.textColor
                                                            : '#101828',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {getPreventorName(visita) ||
                                                    t(
                                                        'expressDrawer.statusTexts.pendingAssignment'
                                                    )}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#98A2B3',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '.04em'
                                                }}
                                            >
                                                {t('expressDrawer.fields.deadline')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, color: '#344054', fontWeight: 600 }}
                                            >
                                                {visita?.fechaLimite
                                                    ? getDate(visita.fechaLimite)
                                                    : '-'}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#98A2B3',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '.04em'
                                                }}
                                            >
                                                {t('expressDrawer.fields.visitDate')}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ mt: 0.5, color: '#344054', fontWeight: 600 }}
                                            >
                                                {getVisitDate(visita)
                                                    ? getDate(getVisitDate(visita))
                                                    : '-'}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {canAssignPreventor && (
                                        <Box sx={{ mt: 2.5 }}>
                                            {isEditing ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5
                                                    }}
                                                >
                                                    <AutocompleteComponent
                                                        value={selectedPreventor}
                                                        options={preventorOptions}
                                                        size="small"
                                                        name={`preventor-${visita.id}`}
                                                        label={t(
                                                            'expressDrawer.fields.preventorSelect'
                                                        )}
                                                        loading={isFetchingPreventors}
                                                        onChange={(value: any) =>
                                                            handleSelectPreventor(visita.id, value)
                                                        }
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        onClick={() =>
                                                            handleAssignPreventor(visita)
                                                        }
                                                        disabled={
                                                            !selectedPreventor?.id || isSaving
                                                        }
                                                        fullWidth
                                                        sx={{
                                                            textTransform: 'none',
                                                            boxShadow: 'none'
                                                        }}
                                                    >
                                                        {isSaving ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <CircularProgress
                                                                    size={16}
                                                                    color="inherit"
                                                                />
                                                                {t(
                                                                    'expressDrawer.loading.assigning'
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            t(
                                                                'expressDrawer.actions.assignPreventor'
                                                            )
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() =>
                                                            handleCancelAssign(visita.id)
                                                        }
                                                        disabled={isSaving}
                                                        fullWidth
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {t('expressDrawer.actions.cancel')}
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        flexDirection: { xs: 'column', sm: 'row' }
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleCancelVisit(visita.id)}
                                                        disabled={isSaving}
                                                        fullWidth
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {tVisitas('drawer.cancelVisit')}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => handleStartAssign(visita.id)}
                                                        disabled={isSaving}
                                                        fullWidth
                                                        sx={{
                                                            textTransform: 'none',
                                                            boxShadow: 'none'
                                                        }}
                                                    >
                                                        {t('expressDrawer.actions.assignPreventor')}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {canReassignPreventor && (
                                        <Box sx={{ mt: 2.5 }}>
                                            {isReassigning ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5
                                                    }}
                                                >
                                                    <AutocompleteComponent
                                                        value={selectedPreventor}
                                                        options={preventorOptions}
                                                        size="small"
                                                        name={`reassign-preventor-${visita.id}`}
                                                        label={t(
                                                            'expressDrawer.fields.preventorSelect'
                                                        )}
                                                        loading={isFetchingPreventors}
                                                        onChange={(value: any) =>
                                                            handleSelectPreventor(visita.id, value)
                                                        }
                                                    />
                                                    <TextField
                                                        label={tVisitas(
                                                            'drawers.assignPreventor.input.reassignationMotive'
                                                        )}
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        value={reassignMotive}
                                                        inputProps={{ maxLength: 300 }}
                                                        onChange={(event) =>
                                                            handleReassignMotiveChange(
                                                                visita.id,
                                                                event.target.value
                                                            )
                                                        }
                                                        helperText={`${reassignMotive.length}/300`}
                                                        disabled={isSaving}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            gap: 1.5,
                                                            flexWrap: 'wrap'
                                                        }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() =>
                                                                resetCardState(visita.id)
                                                            }
                                                            disabled={isSaving}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            {t('expressDrawer.actions.cancel')}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() =>
                                                                handleReassignPreventor(visita)
                                                            }
                                                            disabled={
                                                                !selectedPreventor?.id ||
                                                                reassignMotive.trim().length < 10 ||
                                                                isSaving
                                                            }
                                                            sx={{
                                                                textTransform: 'none',
                                                                boxShadow: 'none',
                                                                minWidth: 160
                                                            }}
                                                        >
                                                            {isSaving ? (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <CircularProgress
                                                                        size={16}
                                                                        color="inherit"
                                                                    />
                                                                    {t(
                                                                        'expressDrawer.loading.assigning'
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                tVisitas(
                                                                    'table.actions.reassignPreventor'
                                                                )
                                                            )}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        alignItems: 'stretch',
                                                        flexWrap: 'nowrap',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleCancelVisit(visita.id)}
                                                        disabled={isSaving}
                                                        sx={{
                                                            textTransform: 'none',
                                                            flex: '1 1 0',
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        {tVisitas('drawer.cancelVisit')}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() =>
                                                            handleStartReassign(visita.id)
                                                        }
                                                        disabled={isSaving}
                                                        sx={{
                                                            textTransform: 'none',
                                                            boxShadow: 'none',
                                                            flex: '1 1 0',
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        {tVisitas(
                                                            'table.actions.reassignPreventor'
                                                        )}
                                                    </Button>
                                                    <IconButton
                                                        onClick={() =>
                                                            handleOpenVisitDetail(visita)
                                                        }
                                                        disabled={isSaving || !canSeeVisitDetail}
                                                        sx={{
                                                            width: 42,
                                                            height: 42,
                                                            border: '1px solid #D0D5DD',
                                                            borderRadius: 1.5
                                                        }}
                                                    >
                                                        <VisibilityOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {!canAssignPreventor &&
                                        !canReassignPreventor &&
                                        !canReviewVisit &&
                                        canSeeVisitDetail && (
                                            <Box
                                                sx={{
                                                    mt: 2.5,
                                                    display: 'flex',
                                                    justifyContent: 'flex-end'
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => handleOpenVisitDetail(visita)}
                                                    startIcon={<VisibilityOutlinedIcon />}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {t('expressDrawer.actions.viewVisit')}
                                                </Button>
                                            </Box>
                                        )}

                                    {canReviewVisit && (
                                        <Box sx={{ mt: 2.5 }}>
                                            {isRejecting ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5
                                                    }}
                                                >
                                                    <TextField
                                                        label={t(
                                                            'expressDrawer.fields.rejectReason'
                                                        )}
                                                        multiline
                                                        rows={4}
                                                        fullWidth
                                                        value={rejectReason}
                                                        inputProps={{ maxLength: 500 }}
                                                        onChange={(event) =>
                                                            handleRejectReasonChange(
                                                                visita.id,
                                                                event.target.value
                                                            )
                                                        }
                                                        helperText={`${rejectReason.length}/500`}
                                                        disabled={isSaving}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'flex-end',
                                                            gap: 1.5,
                                                            flexWrap: 'wrap'
                                                        }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() =>
                                                                resetCardState(visita.id)
                                                            }
                                                            disabled={isSaving}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            {t('expressDrawer.actions.cancel')}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="error"
                                                            onClick={() =>
                                                                handleRejectVisit(visita.id)
                                                            }
                                                            disabled={
                                                                !rejectReason.trim() || isSaving
                                                            }
                                                            sx={{
                                                                textTransform: 'none',
                                                                boxShadow: 'none',
                                                                minWidth: 120
                                                            }}
                                                        >
                                                            {isSaving ? (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <CircularProgress
                                                                        size={16}
                                                                        color="inherit"
                                                                    />
                                                                    {t(
                                                                        'expressDrawer.loading.rejecting'
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                t(
                                                                    'expressDrawer.actions.confirmReject'
                                                                )
                                                            )}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        alignItems: 'stretch'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            flex: '1 1 80%',
                                                            display: 'flex',
                                                            gap: 1.5
                                                        }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() =>
                                                                handleStartReject(visita.id)
                                                            }
                                                            disabled={isSaving}
                                                            sx={{ textTransform: 'none', flex: 1 }}
                                                        >
                                                            {t('expressDrawer.actions.reject')}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() =>
                                                                handleApproveVisit(visita.id)
                                                            }
                                                            disabled={isSaving}
                                                            sx={{
                                                                textTransform: 'none',
                                                                boxShadow: 'none',
                                                                flex: 1
                                                            }}
                                                        >
                                                            {isSaving ? (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                        justifyContent: 'center'
                                                                    }}
                                                                >
                                                                    <CircularProgress
                                                                        size={16}
                                                                        color="inherit"
                                                                    />
                                                                    {t(
                                                                        'expressDrawer.loading.approving'
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                t('expressDrawer.actions.approve')
                                                            )}
                                                        </Button>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            flex: '0 0 20%',
                                                            display: 'flex',
                                                            justifyContent: 'flex-end'
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={(event) =>
                                                                handleOpenVisitMenu(event, visita)
                                                            }
                                                            disabled={
                                                                isSaving ||
                                                                (!canSeeVisitDetail &&
                                                                    !canDownloadActa)
                                                            }
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth: 48,
                                                                border: '1px solid #D0D5DD',
                                                                borderRadius: 1.5
                                                            }}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                </Box>
            </Box>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseVisitMenu}
            >
                {menuVisit && (
                    <>
                        {VISIT_DETAIL_VISIBLE_STATES.includes(Number(menuVisit?.idEstado)) && (
                            <MenuItem onClick={() => handleOpenVisitDetail(menuVisit)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VisibilityOutlinedIcon fontSize="small" />
                                    {t('expressDrawer.actions.viewVisit')}
                                </Box>
                            </MenuItem>
                        )}
                        {Boolean(menuVisit?.id && getVisitTypeId(menuVisit)) && (
                            <MenuItem onClick={() => handleDownloadActa(menuVisit)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DownloadOutlinedIcon fontSize="small" />
                                    {t('expressDrawer.actions.downloadActa')}
                                </Box>
                            </MenuItem>
                        )}
                    </>
                )}
            </Menu>

            <BackdropComponent loading={isCancellingVisit} />
        </DrawerContainer>
    );
}

export default ExpressVisitManagementDrawer;
